import React, { useState, useEffect, useContext } from 'react'
import styled, { css, ThemeContext } from 'styled-components'

// Styles
import { applyTextStyle, applyTextEllipsis } from '../../styles/typography'
import { shadows, fastTransition } from '../../styles/effects'

// Components
import { Icon, IconWrapper } from '../icon'

// Types
import { IFormControlProps } from '../../common/models/IFormControlProps'
import { IconType } from '../icon/IconType'

interface IRadioGroupItem {
  label: string | React.ReactNode
  value: any
  icon?: IconType
  utfImg?: string
  disabled?: boolean
}

export enum Variant {
  Switch = 'switch',
  List = 'list',
  // eslint-disable-next-line @typescript-eslint/no-shadow
  Button = 'button',
}

export interface IProps extends IFormControlProps {
  items: IRadioGroupItem[]
  name: string
  variant?: Variant
  className?: string
  onClick?: (e?: any) => void
  /** Handle for the e2e suite, on the wrapper that carries the click. */
  'data-cy'?: string
}

const RadioGroup = ({ className, 'data-cy': dataCy, ...props }: IProps) => {
  const themeContext = useContext(ThemeContext)
  const [checkedValue, setCheckedValue] = useState<string | number | undefined>(
    props.value
  )

  const errorMessage =
    props.meta && props.meta.error && props.meta.touched
      ? props.meta.error
      : props.errorMessage

  useEffect(() => {
    setCheckedValue(props.value)
  }, [props.value])

  return (
    <RadioGroupWrapper
      disabled={props.disabled}
      errorMessage={errorMessage}
      variant={props.variant}
      className={className}
      data-cy={dataCy}
      onClick={props.disabled ? undefined : props.onClick}
    >
      {props.label && <RadioGroupLabel>{props.label}</RadioGroupLabel>}
      {props.label && props.additionalLabel && (
        <RadioGroupAdditionalLabel>
          {props.additionalLabel}
        </RadioGroupAdditionalLabel>
      )}
      <RadioGroupInnerWrapper>
        <StyledRadioGroup {...props} onClick={undefined}>
          {props.items.map((item) => {
            if (props.variant !== 'button') {
              return (
                <RadioButton key={item.value} variant={props.variant}>
                  <RadioButtonInput
                    disabled={props.disabled || item.disabled}
                    type="radio"
                    checked={checkedValue === item.value}
                    name={props.name}
                    value={item.value}
                    variant={props.variant}
                    onChange={(e) => {
                      setCheckedValue(e.target.value)
                      props.handleChange && props.handleChange(e)
                    }}
                  />
                  <RadioButtonContentWrapper variant={props.variant}>
                    <RadioButtonCheckmark variant={props.variant}>
                      <Icon
                        type="Check"
                        size={9}
                        color={themeContext.colors.lightest}
                      />
                    </RadioButtonCheckmark>
                    {item.icon && (
                      <StyledIcon
                        type={item.icon}
                        size={13}
                        color={themeContext.colors.primary}
                      />
                    )}
                    {item.utfImg && <UtfImg>{item.utfImg}</UtfImg>}
                    <RadioButtonLabel>{item.label}</RadioButtonLabel>
                  </RadioButtonContentWrapper>
                </RadioButton>
              )
            } else {
              return (
                <Button
                  key={item.value}
                  checked={
                    props.type === 'number'
                      ? Number(checkedValue) === Number(item.value)
                      : checkedValue === item.value
                  }
                >
                  <RadioButtonInput
                    disabled={props.disabled || item.disabled}
                    type="radio"
                    checked={checkedValue === item.value}
                    name={props.name}
                    value={item.value}
                    variant={props.variant}
                    onChange={(e) => {
                      setCheckedValue(e.target.value)
                      props.handleChange && props.handleChange(e)
                    }}
                  />
                  {item.label}
                </Button>
              )
            }
          })}
        </StyledRadioGroup>
      </RadioGroupInnerWrapper>
      <RadioGroupError>{errorMessage}</RadioGroupError>
    </RadioGroupWrapper>
  )
}

export const radioSwitchStyles = (grey: boolean, theme: any) => css`
  padding: 0;
  align-self: flex-start;
  margin-left: auto;

  > div > div {
    border-radius: 36px;
    background-color: ${grey ? theme.colors.textLighter : theme.colors.primary};
    border: 2px solid ${grey ? theme.colors.textLighter : theme.colors.primary};

    > div > input:checked + ${RadioButtonContentWrapper} {
      background-color: ${grey ? theme.colors.lightest : theme.colors.lightest};
      box-shadow: ${shadows.depthTwo};

      > label > span > svg > path {
        fill: ${grey
          ? theme.colors.textLighter
          : theme.colors.primary} !important;
      }
    }

    > div > div {
      width: 24px;
      height: 24px;
      padding: 0;
      border-radius: 50%;
    }
  }
`

const RadioGroupLabel = styled.label`
  display: block;
  margin: 0 0 8px;
  padding-left: 1px;
  color: ${({ theme }) => theme.colors.textLight};
  ${applyTextStyle('labelHeavy')}
  ${applyTextEllipsis()}
`

const RadioGroupAdditionalLabel = styled.p`
  ${applyTextStyle('caption')}
  margin: 0 0 8px;
  color: ${({ theme }) => theme.colors.textLight};
`

const Button = styled.div<{ checked: boolean }>`
  position: relative;
  border-radius: 4px;
  padding: 8px 16px;
  margin-bottom: 8px;
  background-color: ${({ checked, theme }) =>
    checked ? theme.colors.primary : theme.colors.primaryLight};
  color: ${({ checked, theme }) =>
    checked ? theme.colors.lightest : theme.colors.primary};
  transition: background-color 240ms ease-in-out, color 240ms ease-in-out;
  ${applyTextStyle('labelHeavy')};

  &:hover {
    background-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.lightest};
  }

  &:not(last-of-type) {
    margin-right: 8px;
  }
`

const RadioButton = styled.div<{ variant?: Variant }>`
  position: relative;
  align-items: center;
  display: flex;
  width: 100%;
  height: ${(props) => (props.variant === Variant.Switch ? '24px' : '48px')};
  border-radius: 8px;
  cursor: pointer;

  ${({ variant }) =>
    variant === Variant.Switch &&
    css`
      width: fit-content;

      label {
        margin: 0;
      }
    `}

  ${(props) =>
    props.variant !== Variant.Switch &&
    css`
      box-shadow: ${shadows.depthTwo};
    `}
`

const RadioButtonCheckmark = styled.div<{ variant?: Variant }>`
  display: ${(props) => (props.variant === Variant.Switch ? 'none' : 'flex')};
  align-items: center;

  ${IconWrapper} {
    display: none;
    z-index: 1;
    left: 3px;
  }

  &:before {
    content: '';
    box-sizing: border-box;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border-radius: 8px; // Must be same as RadioButton
  }

  &:after {
    content: '';
    position: relative;
    display: inline-block;
    box-sizing: border-box;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background-color: ${({ theme }) => theme.colors.primaryLight};
    border: 1px solid ${({ theme }) => theme.colors.primary};
    box-shadow: inset 2px 2px 1px rgba(0, 0, 0, 0.1);
  }
`

const StyledIcon = styled(Icon)`
  flex-shrink: 0;
  margin-right: 8px;

  svg path {
    transition: none;
  }
`

export const RadioButtonContentWrapper = styled.div<{ variant?: Variant }>`
  align-items: center;
  display: flex;
  height: 100%;
  width: 100%;
  padding: ${(props) =>
    props.variant === Variant.Switch ? '0 12px' : '0 16px'};
  transition: color ${fastTransition};
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.primary};
  justify-content: ${({ variant }) =>
    variant === Variant.Switch ? 'center' : 'initial'};
`

const RadioButtonLabel = styled.label`
  ${applyTextStyle('labelHeavy')}
  margin: 0 0 0 8px;
  z-index: 1;
  cursor: pointer;
`

export const RadioButtonInput = styled.input<{ variant?: Variant }>`
  z-index: 100;
  opacity: 0;
  cursor: pointer;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;

  &:disabled {
    cursor: default;

    & + ${RadioButtonContentWrapper} ${RadioButtonCheckmark} {
      opacity: 0.15;

      &:after {
        background-color: ${({ theme }) => theme.colors.primaryDark};
        border-color: ${({ theme }) => theme.colors.primaryDark};
      }
    }

    & + ${RadioButtonContentWrapper} ${RadioButtonLabel} {
      opacity: 0.15;
    }
  }

  &:hover + ${RadioButtonContentWrapper} ${RadioButtonCheckmark}:before {
    background-color: ${({ theme }) => theme.colors.primaryLight};
  }

  &:checked + ${RadioButtonContentWrapper} ${RadioButtonCheckmark} {
    ${IconWrapper} {
      display: inline-block;
    }

    &:before {
      background-color: ${({ theme }) => theme.colors.primaryLight};
    }

    &:after {
      background-color: ${({ theme }) => theme.colors.primaryDark};
      border-color: ${({ theme }) => theme.colors.primaryDark};
      margin-left: -9px;
    }
  }

  ${(props) =>
    props.variant === Variant.Switch &&
    css`
      + ${RadioButtonContentWrapper} {
        color: ${props.theme.colors.text};
      }
      &:hover + ${RadioButtonContentWrapper} {
        color: ${props.theme.colors.primary};
      }
      &:checked + ${RadioButtonContentWrapper} {
        background-color: ${props.theme.colors.primary};
        color: ${props.theme.colors.lightest};

        ${IconWrapper} svg path {
          fill: white;
        }
      }
    `}
`

const UtfImg = styled.p`
  margin: 0 0 0 16px;
  object-fit: contain;
  object-position: center;
  font-size: 20px;
  z-index: 1;
`

const RadioGroupWrapper = styled.div<
  Pick<IFormControlProps, 'disabled' | 'errorMessage'> & { variant?: Variant }
>`
  position: relative;
  padding-bottom: 24px;

  ${(props) => props.variant === Variant.Switch && 'display: inline-block;'}

  ${(props) =>
    props.errorMessage &&
    css`
      ${RadioGroupError} {
        display: block;
      }

      ${RadioButton} {
        box-shadow: none;

        ${RadioButtonCheckmark}:after {
          border-color: ${props.theme.colors.secondary};
        }

        ${RadioButtonCheckmark}:before {
          border: 2px solid ${props.theme.colors.secondary};
        }
      }
    `}

  ${(props) =>
    props.disabled &&
    css`
      ${RadioGroupLabel}, ${RadioGroupAdditionalLabel} {
        pointer-events: none;
        color: ${props.theme.colors.textLighter};
      }
    `}
`

const RadioGroupInnerWrapper = styled.div`
  position: relative;
`

const StyledRadioGroup = styled.div<{ variant?: Variant }>`
  position: relative;

  ${(props) =>
    props.variant === Variant.Switch
      ? css`
          flex-wrap: nowrap;
          align-items: center;
          display: flex;
          border-radius: 8px;
          background-color: ${props.theme.colors.primaryLight};
        `
      : props.variant === Variant.Button
      ? css`
          display: flex;
          align-items: center;
          justify-content: center;
          flex-wrap: wrap;
        `
      : css`
          > *:not(:last-child) {
            margin-bottom: 12px;
          }
        `}
`

const RadioGroupError = styled.p`
  position: absolute;
  display: none;
  margin: 8px 0 0;
  padding-left: 1px;
  color: ${({ theme }) => theme.colors.secondary};
  ${applyTextStyle('label')}
  ${applyTextEllipsis()}
`

export default RadioGroup
